// frontend/src/pages/ErrorDashboard.tsx
// Embeds the Looker Studio error dashboard, sized to fill the available viewport.

import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { BarChart as DashboardIcon } from '@mui/icons-material';

const EMBED_URL =
  'https://datastudio.google.com/embed/reporting/4b912bb4-be74-48d9-94ea-0c8b94794d70/page/th0PF';

const rocheBlue = '#0B41CD';

// Height of the AppBar + any vertical padding applied by the main content Box.
// AppBar is 64px; main Box has p:3 (24px top + 24px bottom) = 48px; title row ≈ 48px.
const CHROME_HEIGHT = 64 + 48 + 48;

const ErrorDashboard: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [iframeHeight, setIframeHeight] = useState<number>(600);
  const [loading, setLoading] = useState(true);

  // Recalculate iframe height whenever the window resizes.
  useEffect(() => {
    const calculate = () => {
      setIframeHeight(Math.max(400, window.innerHeight - CHROME_HEIGHT));
    };
    calculate();
    window.addEventListener('resize', calculate);
    return () => window.removeEventListener('resize', calculate);
  }, []);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <DashboardIcon sx={{ color: rocheBlue, fontSize: 28 }} />
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Error Dashboard
        </Typography>
      </Box>

      {/* Iframe container */}
      <Box
        ref={containerRef}
        sx={{
          position: 'relative',
          width: '100%',
          height: iframeHeight,
          borderRadius: 2,
          overflow: 'hidden',
          border: '1px solid rgba(0,0,0,0.08)',
          bgcolor: 'background.paper',
        }}
      >
        {loading && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'background.paper',
              zIndex: 1,
            }}
          >
            <CircularProgress sx={{ color: rocheBlue }} />
          </Box>
        )}
        <iframe
          title="Error Dashboard"
          src={EMBED_URL}
          width="100%"
          height="100%"
          style={{ border: 'none', display: 'block' }}
          allowFullScreen
          onLoad={() => setLoading(false)}
        />
      </Box>
    </Box>
  );
};

export default ErrorDashboard;
